<template>
  <div class="driver-onboarding-page">
    <div class="page-header">
      <div class="page-header-text">
        <h1 class="page-title">Onboarding kierowców</h1>
      </div>
    </div>
    <v-row>
      <v-col cols="12">
        <v-card class="content-card">
          <v-card-title class="text-h5 primary--text font-weight-bold">
            <v-icon left color="primary">mdi-account-clock</v-icon>
            {{ $t('driverOnboarding.title') }}
            <v-spacer />
            <v-text-field
              v-model="search"
              append-icon="mdi-magnify"
              :label="$t('driverOnboarding.searchPlaceholder')"
              single-line
              hide-details
              dense
              outlined
              style="max-width: 300px"
              @input="debouncedSearch"
            />
          </v-card-title>

          <v-card-text>
            <v-data-table
              :headers="headers"
              :items="drivers"
              :loading="loading"
              :options.sync="options"
              :server-items-length="totalItems"
              :footer-props="{
                'items-per-page-options': [10, 25, 50],
                'items-per-page-text': $t('driverOnboarding.perPage'),
                'page-text': $t('driverOnboarding.pageText')
              }"
              class="elevation-1"
              :loading-text="$t('driverOnboarding.loadingText')"
              :no-data-text="$t('driverOnboarding.noDataText')"
            >
              <!-- Driver Name -->
              <template v-slot:[`item.driver`]="{ item }">
                <div class="d-flex align-center">
                  <v-avatar size="36" class="mr-3" :color="getAvatarColor(item.onboardingStatus)">
                    <v-icon dark small>mdi-account</v-icon>
                  </v-avatar>
                  <div>
                    <div class="font-weight-medium">
                      {{ item.firstName || '—' }} {{ item.lastName || '—' }}
                      <v-chip
                        v-if="item.accountType === 'SUBFLEET_OWNER'"
                        x-small
                        color="amber darken-2"
                        dark
                        class="ml-1"
                      >
                        <v-icon x-small left>mdi-car-key</v-icon>Właściciel aut
                      </v-chip>
                    </div>
                    <div class="text-caption grey--text">{{ item.email }}</div>
                    <!-- Notatka biura widoczna wprost na liście — po to jest. -->
                    <div v-if="item.onboardingNote" class="text-caption amber--text text--lighten-2">
                      <v-icon x-small color="amber">mdi-note-text-outline</v-icon>
                      {{ item.onboardingNote.slice(0, 90) }}{{ item.onboardingNote.length > 90 ? '…' : '' }}
                    </div>
                    <div v-if="item.accountType === 'SUBFLEET_OWNER' && item.ownerCarsInfo" class="text-caption amber--text text--lighten-2">
                      🚗 {{ item.ownerCarsInfo.slice(0, 80) }}{{ item.ownerCarsInfo.length > 80 ? '…' : '' }}
                    </div>
                  </div>
                </div>
              </template>

              <!-- Phone -->
              <template v-slot:[`item.phone`]="{ item }">
                <span class="text-body-2">{{ item.phone || '—' }}</span>
              </template>

              <!-- City -->
              <template v-slot:[`item.city`]="{ item }">
                <span class="text-body-2">{{ item.city || '—' }}</span>
              </template>

              <!-- Onboarding Status -->
              <template v-slot:[`item.onboardingStatus`]="{ item }">
                <v-chip
                  small
                  :color="getStatusColor(item.onboardingStatus)"
                  text-color="white"
                >
                  {{ getStatusLabel(item.onboardingStatus) }}
                </v-chip>
              </template>

              <!-- Progress -->
              <template v-slot:[`item.progress`]="{ item }">
                <v-progress-linear
                  :value="getProgress(item.onboardingStatus)"
                  :color="getStatusColor(item.onboardingStatus)"
                  height="8"
                  rounded
                  style="min-width: 80px"
                />
                <div class="text-caption text-center mt-1">{{ getProgress(item.onboardingStatus) }}%</div>
              </template>

              <!-- Register Type -->
              <template v-slot:[`item.registerType`]="{ item }">
                <span class="text-body-2">{{ getRegisterTypeLabel(item.registerType) }}</span>
              </template>

              <!-- Date -->
              <template v-slot:[`item.createdAt`]="{ item }">
                <div>
                  <div class="text-body-2">{{ formatDate(item.createdAt) }}</div>
                  <div class="text-caption grey--text">{{ formatTimeAgo(item.createdAt) }}</div>
                </div>
              </template>

              <!-- Actions -->
              <template v-slot:[`item.actions`]="{ item }">
                <!-- Notatka i kontakt wprost z listy — bez otwierania zgłoszenia. -->
                <v-btn icon small class="mr-1" :color="item.onboardingNote ? 'amber' : 'grey'"
                  :title="item.onboardingNote ? item.onboardingNote : 'Dodaj notatkę'"
                  @click.stop="openNoteDialog(item)">
                  <v-icon small>{{ item.onboardingNote ? 'mdi-note-text' : 'mdi-note-plus-outline' }}</v-icon>
                </v-btn>
                <v-btn icon small class="mr-1" color="info" :disabled="!item.email"
                  title="Wyślij e-mail" @click.stop="openContactDialog('email', item)">
                  <v-icon small>mdi-email-outline</v-icon>
                </v-btn>
                <v-btn icon small class="mr-1" color="info" :disabled="!item.phone"
                  title="Wyślij SMS" @click.stop="openContactDialog('sms', item)">
                  <v-icon small>mdi-message-text-outline</v-icon>
                </v-btn>
                <v-btn small color="info" @click="viewDriver(item)" class="mr-1">
                  <v-icon left small>mdi-eye</v-icon>
                  {{ $t('driverOnboarding.preview') }}
                </v-btn>
                <v-btn
                  v-if="item.onboardingStatus !== 'READY_TO_WORK'"
                  small
                  color="success"
                  @click="openApproveDialog(item)"
                  class="mr-1"
                  :loading="processing.includes(item.id)"
                >
                  <v-icon left small>mdi-check</v-icon>
                  {{ $t('driverOnboarding.accept') }}
                </v-btn>
                <v-btn
                  v-if="item.onboardingStatus !== 'READY_TO_WORK'"
                  small
                  color="error"
                  @click="openRejectDialog(item)"
                  class="mr-1"
                  :loading="processing.includes(item.id)"
                >
                  <v-icon left small>mdi-close</v-icon>
                  {{ $t('driverOnboarding.reject') }}
                </v-btn>
                <!-- „Nie zostaje kierowcą, ale kontakt zostaje" — schodzi z listy
                     i ląduje w książce kontaktów razem ze zgłoszeniami ze strony. -->
                <v-btn
                  v-if="item.onboardingStatus !== 'READY_TO_WORK'"
                  small
                  color="amber darken-2"
                  dark
                  @click="openLeadDialog(item)"
                  :loading="processing.includes(item.id)"
                  title="Zdejmij z listy i zapisz kontakt w książce leadów"
                >
                  <v-icon left small>mdi-account-arrow-right-outline</v-icon>
                  Do kontaktów
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- ==================== DETAIL DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="detailDialog" max-width="1100" scrollable>
      <v-card v-if="selectedDriver">
        <v-card-title class="primary white--text">
          <v-icon left dark>mdi-account-details</v-icon>
          {{ selectedDriver.firstName || '—' }} {{ selectedDriver.lastName || '—' }}
          <v-spacer />
          <v-chip dark small :color="getStatusColor(selectedDriver.onboardingStatus)" class="ml-2">
            {{ getStatusLabel(selectedDriver.onboardingStatus) }}
          </v-chip>
          <v-btn icon dark @click="detailDialog = false" class="ml-2">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pt-4" style="max-height: 70vh">
          <v-tabs v-model="detailTab" background-color="transparent">
            <v-tab>{{ $t('driverOnboarding.tabs.personalData') }}</v-tab>
            <v-tab>{{ $t('driverOnboarding.tabs.documents') }} ({{ (selectedDriver.onboardingDocuments || []).length }})</v-tab>
            <v-tab>{{ $t('driverOnboarding.tabs.zus') }}</v-tab>
            <v-tab>{{ $t('driverOnboarding.tabs.consents') }}</v-tab>
          </v-tabs>

          <v-tabs-items v-model="detailTab" class="mt-4" touchless>
            <!-- TAB: Personal Data -->
            <v-tab-item>
              <div class="d-flex justify-end mb-3">
                <v-btn
                  v-if="!editMode"
                  small
                  color="primary"
                  outlined
                  @click="startEdit"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  {{ $t('driverOnboarding.editData') }}
                </v-btn>
                <template v-else>
                  <v-btn small color="success" @click="saveEdit" :loading="editSaving" class="mr-2">
                    <v-icon left small>mdi-content-save</v-icon>
                    {{ $t('common.save') }}
                  </v-btn>
                  <v-btn small outlined @click="cancelEdit">
                    {{ $t('common.cancel') }}
                  </v-btn>
                </template>
              </div>

              <!-- READ MODE -->
              <v-row v-if="!editMode">
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.contactData') }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.email') }}:</span> {{ selectedDriver.email }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.phone') }}:</span> {{ selectedDriver.phone }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.emailVerified') }}:</span>
                      <v-icon small :color="selectedDriver.emailVerified ? 'success' : 'error'">
                        {{ selectedDriver.emailVerified ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                    </div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.phoneVerified') }}:</span>
                      <v-icon small :color="selectedDriver.phoneVerified ? 'success' : 'error'">
                        {{ selectedDriver.phoneVerified ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                    </div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.basicData') }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.firstName') }}:</span> {{ selectedDriver.firstName || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.lastName') }}:</span> {{ selectedDriver.lastName || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.citizenship') }}:</span> {{ selectedDriver.citizenship || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.language') }}:</span> {{ selectedDriver.mainLanguage || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.dateOfBirth') }}:</span> {{ selectedDriver.dateOfBirth ? formatDate(selectedDriver.dateOfBirth) : '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.ownVehicle') }}:</span> {{ selectedDriver.hasOwnVehicle ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                    <template v-if="selectedDriver.hasOwnVehicle">
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.vehicleMake') }}:</span> {{ selectedDriver.vehicleMake || '—' }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.vehicleModel') }}:</span> {{ selectedDriver.vehicleModel || '—' }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.vehicleYear') }}:</span> {{ selectedDriver.vehicleYear || '—' }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.vehiclePlate') }}:</span> {{ selectedDriver.vehiclePlate || '—' }}</div>
                    </template>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.hasBusiness') }}:</span> {{ selectedDriver.hasBusiness ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.registerType') }}:</span> {{ getRegisterTypeLabel(selectedDriver.registerType) }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.referralCode') }}:</span> {{ selectedDriver.referralCode || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.bankAccount') }}:</span> {{ selectedDriver.accountNo || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.payoutType') }}:</span> {{ selectedDriver.payoutType || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.address') }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.street') }}:</span> {{ selectedDriver.streetAddress || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.homeNumber') }}:</span> {{ selectedDriver.homeNumber || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.aptNumber') }}:</span> {{ selectedDriver.aptNumber || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.postalCode') }}:</span> {{ selectedDriver.postalCode || '—' }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.city') }}:</span> {{ selectedDriver.city || '—' }}</div>
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.registration') }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.registrationDate') }}:</span> {{ formatDate(selectedDriver.createdAt) }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.status') }}:</span> {{ getStatusLabel(selectedDriver.onboardingStatus) }}</div>
                    <div class="detail-row"><span class="label">{{ $t('driverOnboarding.labels.assignedCompany') }}:</span> {{ selectedDriver.companyId || $t('driverOnboarding.none') }}</div>
                  </v-card>
                </v-col>
              </v-row>

              <!-- EDIT MODE -->
              <v-row v-else>
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.contactData') }}</div>
                    <v-text-field v-model="editForm.email" :label="$t('driverOnboarding.labels.email')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.phone" :label="$t('driverOnboarding.labels.phone')" dense outlined />
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.basicData') }}</div>
                    <v-text-field v-model="editForm.firstName" :label="$t('driverOnboarding.labels.firstName')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.lastName" :label="$t('driverOnboarding.labels.lastName')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.citizenship" :label="$t('driverOnboarding.labels.citizenship')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.mainLanguage" :label="$t('driverOnboarding.labels.language')" dense outlined class="mb-2" />
                    <v-switch v-model="editForm.hasOwnVehicle" :label="$t('driverOnboarding.labels.ownVehicle')" dense class="mt-0" />
                    <v-switch v-model="editForm.hasBusiness" :label="$t('driverOnboarding.labels.businessActivity')" dense class="mt-0" />
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.address') }}</div>
                    <v-text-field v-model="editForm.streetAddress" :label="$t('driverOnboarding.labels.street')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.homeNumber" :label="$t('driverOnboarding.labels.homeNumber')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.aptNumber" :label="$t('driverOnboarding.labels.aptNumber')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.postalCode" :label="$t('driverOnboarding.labels.postalCode')" dense outlined class="mb-2" />
                    <v-text-field v-model="editForm.city" :label="$t('driverOnboarding.labels.city')" dense outlined />
                  </v-card>
                </v-col>
                <v-col cols="6">
                  <v-card outlined class="pa-4">
                    <div class="text-subtitle-2 mb-3 font-weight-bold">{{ $t('driverOnboarding.onboardingStatus') }}</div>
                    <v-select
                      v-model="editForm.onboardingStatus"
                      :items="onboardingStatusOptions"
                      item-text="text"
                      item-value="value"
                      :label="$t('common.status')"
                      dense
                      outlined
                    />
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- TAB: Documents -->
            <v-tab-item>
              <!-- Add document button -->
              <div class="d-flex justify-end mb-3">
                <v-btn small color="primary" outlined @click="openAddDocDialog">
                  <v-icon left small>mdi-plus</v-icon>
                  {{ $t('driverOnboarding.addDocument') }}
                </v-btn>
              </div>

              <div v-if="!selectedDriver.onboardingDocuments || selectedDriver.onboardingDocuments.length === 0" class="text-center py-8 grey--text">
                <v-icon large color="grey lighten-1">mdi-file-document-outline</v-icon>
                <div class="mt-2">{{ $t('driverOnboarding.noDocuments') }}</div>
              </div>

              <v-row v-else>
                <v-col
                  v-for="doc in selectedDriver.onboardingDocuments"
                  :key="doc.id"
                  cols="12"
                  md="6"
                >
                  <v-card outlined>
                    <v-card-title class="text-subtitle-1 pb-1">
                      <v-icon left small>mdi-file-document</v-icon>
                      {{ getDocTypeLabel(doc.documentType) }}
                      <v-spacer />
                      <v-chip x-small :color="getDocStatusColor(doc.status)" text-color="white">
                        {{ getDocStatusLabel(doc.status) }}
                      </v-chip>
                    </v-card-title>
                    <v-card-text class="pb-2">
                      <div class="text-caption grey--text">{{ doc.originalName || $t('driverOnboarding.file') }}</div>
                      <div v-if="doc.expiryDate" class="text-caption">{{ $t('driverOnboarding.validUntil') }}: {{ formatDate(doc.expiryDate) }}</div>
                      <div v-if="doc.rejectionReason" class="text-caption error--text mt-1">
                        {{ $t('driverOnboarding.rejectionReason') }}: {{ doc.rejectionReason }}
                      </div>
                    </v-card-text>
                    <v-card-actions>
                      <v-btn small text color="info" @click="previewDocument(doc)" :loading="docLoading === doc.id">
                        <v-icon left small>mdi-eye</v-icon>
                        {{ $t('driverOnboarding.preview') }}
                      </v-btn>
                      <v-spacer />
                      <v-btn
                        v-if="doc.status !== 'VERIFIED'"
                        small
                        text
                        color="success"
                        @click="verifyDocument(doc)"
                        :loading="docProcessing.includes(doc.id)"
                      >
                        <v-icon left small>mdi-check</v-icon>
                        OK
                      </v-btn>
                      <v-btn
                        v-if="doc.status !== 'REJECTED'"
                        small
                        text
                        color="error"
                        @click="openRejectDocDialog(doc)"
                        :loading="docProcessing.includes(doc.id)"
                      >
                        <v-icon left small>mdi-close</v-icon>
                        {{ $t('driverOnboarding.reject') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <!-- TAB: ZUS -->
            <v-tab-item>
              <div class="d-flex justify-end mb-3">
                <v-btn
                  v-if="!zusEditMode"
                  small
                  color="primary"
                  outlined
                  @click="startZusEdit"
                >
                  <v-icon left small>mdi-pencil</v-icon>
                  {{ selectedDriver.zusDeclarations ? $t('driverOnboarding.editZus') : $t('driverOnboarding.addZus') }}
                </v-btn>
                <template v-else>
                  <v-btn small color="success" @click="saveZusEdit" :loading="zusSaving" class="mr-2">
                    <v-icon left small>mdi-content-save</v-icon>
                    {{ $t('common.save') }}
                  </v-btn>
                  <v-btn small outlined @click="zusEditMode = false">{{ $t('common.cancel') }}</v-btn>
                </template>
              </div>

              <!-- ZUS READ MODE -->
              <template v-if="!zusEditMode">
                <div v-if="!selectedDriver.zusDeclarations" class="text-center py-8 grey--text">
                  <v-icon large color="grey lighten-1">mdi-file-sign</v-icon>
                  <div class="mt-2">{{ $t('driverOnboarding.noZusDeclarations') }}</div>
                </div>
                <v-card v-else outlined class="pa-4">
                  <v-row>
                    <v-col cols="6">
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.employedElsewhere') }}:</span> {{ selectedDriver.zusDeclarations.isEmployedElsewhere ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.earnsAboveMinimum') }}:</span> {{ selectedDriver.zusDeclarations.earnsAboveMinimum ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.student') }}:</span> {{ selectedDriver.zusDeclarations.isStudent ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.pensionRights') }}:</span> {{ selectedDriver.zusDeclarations.hasPensionRights ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                    </v-col>
                    <v-col cols="6">
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.rentRights') }}:</span> {{ selectedDriver.zusDeclarations.hasRentRights ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.voluntarySickness') }}:</span> {{ selectedDriver.zusDeclarations.wantsVoluntarySickness ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.registeredUnemployed') }}:</span> {{ selectedDriver.zusDeclarations.isRegisteredUnemployed ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.disabilityCert') }}:</span> {{ selectedDriver.zusDeclarations.hasDisabilityCert ? $t('driverOnboarding.yes') : $t('driverOnboarding.no') }}</div>
                    </v-col>
                    <v-col cols="6" v-if="selectedDriver.zusDeclarations.taxOffice">
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.taxOffice') }}:</span> {{ selectedDriver.zusDeclarations.taxOffice }}</div>
                    </v-col>
                    <v-col cols="6" v-if="selectedDriver.zusDeclarations.nfzBranch">
                      <div class="detail-row"><span class="label">{{ $t('driverOnboarding.zus.nfzBranch') }}:</span> {{ selectedDriver.zusDeclarations.nfzBranch }}</div>
                    </v-col>
                  </v-row>
                </v-card>
              </template>

              <!-- ZUS EDIT MODE -->
              <v-card v-if="zusEditMode" outlined class="pa-4">
                <v-row>
                  <v-col cols="6">
                    <v-switch v-model="zusForm.isEmployedElsewhere" :label="$t('driverOnboarding.zus.employedElsewhere')" dense class="mt-0" />
                    <v-switch v-model="zusForm.earnsAboveMinimum" :label="$t('driverOnboarding.zus.earnsAboveMinimum')" dense class="mt-0" />
                    <v-switch v-model="zusForm.isStudent" :label="$t('driverOnboarding.zus.student')" dense class="mt-0" />
                    <v-switch v-model="zusForm.hasPensionRights" :label="$t('driverOnboarding.zus.pensionRights')" dense class="mt-0" />
                  </v-col>
                  <v-col cols="6">
                    <v-switch v-model="zusForm.hasRentRights" :label="$t('driverOnboarding.zus.rentRights')" dense class="mt-0" />
                    <v-switch v-model="zusForm.wantsVoluntarySickness" :label="$t('driverOnboarding.zus.voluntarySickness')" dense class="mt-0" />
                    <v-switch v-model="zusForm.isRegisteredUnemployed" :label="$t('driverOnboarding.zus.registeredUnemployed')" dense class="mt-0" />
                    <v-switch v-model="zusForm.hasDisabilityCert" :label="$t('driverOnboarding.zus.disabilityCert')" dense class="mt-0" />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="zusForm.taxOffice" :label="$t('driverOnboarding.zus.taxOffice')" dense outlined />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field v-model="zusForm.nfzBranch" :label="$t('driverOnboarding.zus.nfzBranch')" dense outlined />
                  </v-col>
                </v-row>
              </v-card>
            </v-tab-item>

            <!-- TAB: Consents -->
            <v-tab-item>
              <v-card outlined class="pa-4">
                <div
                  v-for="consent in (selectedDriver.consents || [])"
                  :key="consent.consentType"
                  class="detail-row"
                >
                  <v-icon small :color="consent.accepted ? 'success' : 'error'" class="mr-2">
                    {{ consent.accepted ? 'mdi-check-circle' : 'mdi-close-circle' }}
                  </v-icon>
                  <span class="label">{{ getConsentLabel(consent.consentType) }}:</span>
                  {{ consent.accepted ? $t('driverOnboarding.accepted') : $t('driverOnboarding.notAccepted') }}
                </div>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-card-actions class="pa-4" v-if="selectedDriver.onboardingStatus !== 'READY_TO_WORK'">
          <v-spacer />
          <v-btn
            color="success"
            @click="openApproveDialog(selectedDriver)"
            :loading="processing.includes(selectedDriver.id)"
            large
          >
            <v-icon left>mdi-check</v-icon>
            {{ $t('driverOnboarding.acceptDriver') }}
          </v-btn>
          <v-btn
            color="error"
            @click="openRejectDialog(selectedDriver)"
            :loading="processing.includes(selectedDriver.id)"
            large
          >
            <v-icon left>mdi-close</v-icon>
            {{ $t('driverOnboarding.rejectDriver') }}
          </v-btn>
          <v-btn
            color="amber darken-2"
            dark
            @click="openLeadDialog(selectedDriver)"
            :loading="processing.includes(selectedDriver.id)"
            large
            class="ml-2"
          >
            <v-icon left>mdi-account-arrow-right-outline</v-icon>
            Do kontaktów
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ============ PRZENIESIENIE DO KSIĄŻKI KONTAKTÓW ============ -->
    <v-dialog content-class="dark-form-dialog" v-model="leadDialog" max-width="520">
      <v-card>
        <v-card-title class="text-h6 amber--text text--darken-2">
          <v-icon left color="amber darken-2">mdi-account-arrow-right-outline</v-icon>
          Przenieś do kontaktów
        </v-card-title>
        <v-card-text v-if="actionDriver">
          <p>
            <strong>{{ actionDriver.firstName }} {{ actionDriver.lastName }}</strong>
            zniknie z listy do akceptacji, a jego dane trafią do
            <strong>Zgłoszeń ze strony</strong> — tam, gdzie biuro prowadzi kontakty
            do osób chętnych na wynajem.
          </p>
          <p class="text-caption grey--text mb-0">
            Kierowca idzie do archiwum, więc da się go przywrócić. Odrzucenie to co
            innego: zostawia go na liście z prośbą o poprawienie dokumentów.
          </p>
          <v-textarea
            v-model="leadNote"
            label="Notatka — czemu nie zostaje kierowcą (opcjonalnie)"
            rows="3"
            outlined
            dense
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="leadDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="amber darken-2" dark @click="moveDriverToLeads" :loading="leadLoading">
            <v-icon left>mdi-account-arrow-right-outline</v-icon>
            Przenieś do kontaktów
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== NOTE DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="noteDialog.show" max-width="520">
      <v-card v-if="noteDialog.driver">
        <v-card-title>
          <v-icon left color="amber">mdi-note-edit-outline</v-icon>
          Notatka — {{ noteDialog.driver.firstName }} {{ noteDialog.driver.lastName }}
        </v-card-title>
        <v-card-text>
          <v-textarea v-model="noteDraft" rows="3" auto-grow dense outlined hide-details autofocus
            placeholder="np. czeka na badania, umówiony na piątek — widoczne tylko dla biura" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="noteDialog.show = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :loading="noteSaving" @click="saveOnboardingNote">Zapisz</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== CONTACT DIALOG (e-mail / SMS) ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="contactDialog.show" max-width="520">
      <v-card v-if="contactDialog.driver">
        <v-card-title>
          {{ contactDialog.mode === 'sms' ? 'SMS do kandydata' : 'E-mail do kandydata' }}
        </v-card-title>
        <v-card-text>
          <div class="mb-3 grey--text">
            {{ contactDialog.driver.firstName }} {{ contactDialog.driver.lastName }} ·
            {{ contactDialog.mode === 'sms' ? contactDialog.driver.phone : contactDialog.driver.email }}
          </div>
          <v-select
            :items="contactTemplateOptions"
            item-text="name"
            item-value="id"
            label="Szablon (opcjonalnie)"
            dense outlined clearable hide-details class="mb-3"
            @change="applyContactTemplate"
          />
          <v-text-field v-if="contactDialog.mode === 'email'" v-model="contactDialog.subject"
            label="Temat" dense outlined />
          <v-textarea v-model="contactDialog.message" label="Treść" rows="4" auto-grow dense outlined
            :counter="contactDialog.mode === 'sms' ? 320 : undefined" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="contactDialog.show = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="primary" :loading="contactDialog.sending"
            :disabled="!contactDialog.message.trim() || (contactDialog.mode === 'email' && !contactDialog.subject.trim())"
            @click="sendContact">
            Wyślij
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== APPROVE DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="approveDialog" max-width="550">
      <v-card>
        <v-card-title class="text-h6 success--text">
          <v-icon left color="success">mdi-check-circle</v-icon>
          {{ $t('driverOnboarding.driverAcceptance') }}
        </v-card-title>
        <v-card-text v-if="actionDriver">
          <p>{{ $t('driverOnboarding.acceptingDriver') }} <strong>{{ actionDriver.firstName }} {{ actionDriver.lastName }}</strong> ({{ actionDriver.email }}).</p>

          <v-select
            v-model="approveCompanyId"
            :items="companies"
            item-text="name"
            item-value="id"
            :label="$t('driverOnboarding.company') + ' *'"
            outlined
            dense
            class="mt-4"
            :rules="[v => !!v || $t('driverOnboarding.selectCompany')]"
            :loading="companiesLoading"
            :hint="!approveCompanyId && !companiesLoading ? 'Wybierz firmę — bez tego nie da się zaakceptować' : ''"
            persistent-hint
          />

          <v-select
            v-model="approveSubCompanyIds"
            :items="subCompanies"
            item-text="name"
            item-value="id"
            :label="$t('driverOnboarding.branch')"
            outlined
            dense
            multiple
            chips
            small-chips
            deletable-chips
            :loading="subCompaniesLoading"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="approveDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="success" @click="approveDriver" :loading="approveLoading" :disabled="!approveCompanyId">
            <v-icon left>mdi-check</v-icon>
            {{ $t('driverOnboarding.accept') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== REJECT DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="rejectDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-close-circle</v-icon>
          {{ $t('driverOnboarding.driverRejection') }}
        </v-card-title>
        <v-card-text v-if="actionDriver">
          <p>{{ $t('driverOnboarding.confirmReject') }} <strong>{{ actionDriver.firstName }} {{ actionDriver.lastName }}</strong>?</p>
          <v-textarea
            v-model="rejectReason"
            :label="$t('driverOnboarding.rejectionReasonOptional')"
            rows="3"
            outlined
            dense
            class="mt-3"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rejectDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="rejectDriver" :loading="rejectLoading">
            <v-icon left>mdi-close</v-icon>
            {{ $t('driverOnboarding.reject') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== REJECT DOCUMENT DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="rejectDocDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h6 error--text">
          <v-icon left color="error">mdi-file-cancel</v-icon>
          {{ $t('driverOnboarding.documentRejection') }}
        </v-card-title>
        <v-card-text v-if="actionDocument">
          <p>{{ $t('driverOnboarding.rejectDocumentLabel') }}: <strong>{{ getDocTypeLabel(actionDocument.documentType) }}</strong></p>
          <v-textarea
            v-model="docRejectReason"
            :label="$t('driverOnboarding.rejectionReasonRequired')"
            rows="3"
            outlined
            dense
            class="mt-3"
            :rules="[v => !!v || $t('driverOnboarding.provideRejectionReason')]"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="rejectDocDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn color="error" @click="rejectDocument" :loading="docRejectLoading" :disabled="!docRejectReason">
            {{ $t('driverOnboarding.rejectDocument') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== ADD DOCUMENT DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="addDocDialog" max-width="550">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="primary">mdi-file-plus</v-icon>
          {{ $t('driverOnboarding.addDocumentTitle') }}
        </v-card-title>
        <v-card-text>
          <v-select
            v-model="addDocForm.documentType"
            :items="docTypeOptions"
            item-text="text"
            item-value="value"
            :label="$t('driverOnboarding.documentType') + ' *'"
            outlined
            dense
            class="mb-3"
          />
          <v-file-input
            v-model="addDocForm.file"
            :label="$t('driverOnboarding.fileLabel') + ' *'"
            outlined
            dense
            accept="image/jpeg,image/png,image/webp,application/pdf"
            show-size
            prepend-icon="mdi-paperclip"
            class="mb-3"
          />
          <v-text-field
            v-model="addDocForm.documentNumber"
            :label="$t('driverOnboarding.documentNumber')"
            outlined
            dense
            class="mb-3"
          />
          <v-text-field
            v-if="addDocMaWaznosc"
            v-model="addDocForm.expiryDate"
            :label="$t('driverOnboarding.expiryDate')"
            outlined
            dense
            type="date"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="addDocDialog = false">{{ $t('common.cancel') }}</v-btn>
          <v-btn
            color="primary"
            @click="uploadDocument"
            :loading="addDocLoading"
            :disabled="!addDocForm.documentType || !addDocForm.file"
          >
            <v-icon left>mdi-upload</v-icon>
            {{ $t('driverOnboarding.upload') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ==================== DOCUMENT PREVIEW DIALOG ==================== -->
    <v-dialog content-class="dark-form-dialog" v-model="previewDialog" max-width="900">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-file-eye</v-icon>
          {{ previewDocName }}
          <v-spacer />
          <v-btn icon @click="previewDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="text-center pa-4" style="min-height: 400px">
          <div v-if="previewLoading" class="d-flex justify-center align-center" style="height: 400px">
            <v-progress-circular indeterminate color="primary" size="64" />
          </div>
          <img
            v-else-if="previewType === 'image'"
            :src="previewSrc"
            style="max-width: 100%; max-height: 70vh; object-fit: contain"
          />
          <iframe
            v-else-if="previewType === 'pdf'"
            :src="previewSrc"
            style="width: 100%; height: 70vh; border: none"
          />
          <div v-else class="py-8 grey--text">
            {{ $t('driverOnboarding.cannotPreview') }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import { maDateWaznosci } from '@/utils/dokumentyBezWaznosci';

export default {
  name: 'DriverOnboardingPage',
  data() {
    return {
      // Notatka biura i szybki kontakt (e-mail/SMS) przy akceptacji.
      noteDraft: '',
      noteSaving: false,
      noteDialog: { show: false, driver: null },
      // Zapisane szablony z zakładki SMS (sms/templates) — doładowywane raz.
      savedSmsTemplates: [],
      // Wbudowane szablony kontaktu przy akceptacji. {imie} = imię kandydata.
      builtinContactTemplates: [
        {
          id: 'no-contact',
          name: 'Brak kontaktu — odezwij się',
          sms: 'Dzień dobry {imie}, próbowaliśmy się z Tobą skontaktować w sprawie rejestracji w BTP. Oddzwoń: 52 516 11 12. Pozdrawiamy, BTPartner',
          emailSubject: 'BTP — próbowaliśmy się skontaktować',
          emailBody: 'Dzień dobry {imie},\n\npróbowaliśmy się z Tobą skontaktować w sprawie Twojej rejestracji w BTP, ale bez powodzenia. Prosimy o kontakt z biurem: 52 516 11 12 (pon.–pt. 9:00–17:00) lub odpowiedź na tę wiadomość.\n\nPozdrawiamy,\nBTPartner',
        },
        {
          id: 'finish-registration',
          name: 'Dokończ rejestrację',
          sms: 'Dzień dobry {imie}, Twoja rejestracja w BTP jest prawie gotowa. Dokończ ją na btpapp.btpartner.pl. Pytania? 52 516 11 12. BTPartner',
          emailSubject: 'BTP — dokończ rejestrację',
          emailBody: 'Dzień dobry {imie},\n\nTwoja rejestracja w BTP jest prawie gotowa — zostało tylko kilka kroków. Zaloguj się na btpapp.btpartner.pl i dokończ zgłoszenie.\n\nW razie pytań: 52 516 11 12.\n\nPozdrawiamy,\nBTPartner',
        },
        {
          id: 'missing-docs',
          name: 'Brakujące dokumenty',
          sms: 'Dzień dobry {imie}, do zakończenia rejestracji w BTP brakuje dokumentów. Wgraj je na btpapp.btpartner.pl lub zadzwoń: 52 516 11 12. BTPartner',
          emailSubject: 'BTP — brakujące dokumenty do rejestracji',
          emailBody: 'Dzień dobry {imie},\n\ndo zakończenia Twojej rejestracji w BTP brakuje dokumentów. Wgraj je w aplikacji btpapp.btpartner.pl (zakładka Dokumenty) albo skontaktuj się z biurem: 52 516 11 12.\n\nPozdrawiamy,\nBTPartner',
        },
        {
          id: 'office-invite',
          name: 'Zaproszenie do biura',
          sms: 'Dzień dobry {imie}, zapraszamy do biura BTP: ul. Karola Szajnochy 11 lok. 210, Bydgoszcz (pon.–pt. 9:00–17:00) w celu dokończenia formalności. BTPartner',
          emailSubject: 'BTP — zaproszenie do biura',
          emailBody: 'Dzień dobry {imie},\n\nzapraszamy do naszego biura w celu dokończenia formalności:\nul. Karola Szajnochy 11 lok. 210, 85-739 Bydgoszcz\npon.–pt. 9:00–17:00\n\nProsimy zabrać ze sobą dokument tożsamości i prawo jazdy.\n\nPozdrawiamy,\nBTPartner',
        },
      ],
      contactDialog: { show: false, mode: 'email', driver: null, subject: '', message: '', sending: false },
      loading: false,
      drivers: [],
      totalItems: 0,
      search: '',
      searchTimeout: null,

      options: {
        page: 1,
        itemsPerPage: 25,
      },

      headers: [
        { text: 'Kierowca', value: 'driver', sortable: false, width: '250px' },
        { text: 'Telefon', value: 'phone', sortable: false, width: '140px' },
        { text: 'Miasto', value: 'city', sortable: false, width: '120px' },
        { text: 'Status', value: 'onboardingStatus', sortable: false, width: '180px' },
        { text: 'Postęp', value: 'progress', sortable: false, width: '120px' },
        { text: 'Jak do nas trafiłeś', value: 'registerType', sortable: false, width: '180px' },
        { text: 'Data rejestracji', value: 'createdAt', sortable: true, width: '150px' },
        { text: 'Akcje', value: 'actions', sortable: false, width: '300px' },
      ],

      processing: [],

      // Detail dialog
      detailDialog: false,
      selectedDriver: null,
      detailTab: 0,
      detailLoading: false,

      // Edit mode
      editMode: false,
      editSaving: false,
      editForm: {},
      onboardingStatusOptions: [
        { text: 'Rozpoczęta', value: 'REGISTRATION_STARTED' },
        { text: 'Email zweryfikowany', value: 'EMAIL_VERIFIED' },
        { text: 'Konto aktywne', value: 'ACCOUNT_ACTIVE' },
        { text: 'Dane podstawowe', value: 'BASIC_DATA_COMPLETED' },
        { text: 'Dane osobowe', value: 'PERSONAL_DATA_COMPLETED' },
        { text: 'Adres uzupełniony', value: 'ADDRESS_COMPLETED' },
        { text: 'Dokumenty przesłane', value: 'DOCUMENTS_SUBMITTED' },
        { text: 'Dokumenty zweryfikowane', value: 'DOCUMENTS_VERIFIED' },
        { text: 'ZUS wypełniony', value: 'ZUS_DECLARATIONS_COMPLETED' },
        { text: 'Zaakceptowany', value: 'READY_TO_WORK' },
      ],

      // Companies
      companies: [],
      subCompanies: [],
      companiesLoading: false,
      subCompaniesLoading: false,

      // Approve
      approveDialog: false,
      approveLoading: false,
      approveCompanyId: null,
      approveSubCompanyIds: [],
      actionDriver: null,

      // Reject
      rejectDialog: false,
      rejectLoading: false,
      rejectReason: '',
      leadDialog: false,
      leadLoading: false,
      leadNote: '',

      // Document actions
      docProcessing: [],
      docLoading: null,

      // Reject document
      rejectDocDialog: false,
      docRejectLoading: false,
      docRejectReason: '',
      actionDocument: null,

      // Add document
      addDocDialog: false,
      addDocLoading: false,
      addDocForm: {
        documentType: null,
        file: null,
        documentNumber: '',
        expiryDate: '',
      },

      // Document preview
      previewDialog: false,
      previewSrc: '',
      previewType: '',
      previewDocName: '',
      previewLoading: false,

      // ZUS edit
      zusEditMode: false,
      zusSaving: false,
      zusForm: {},

      // Maps
      statusLabels: {
        REGISTRATION_STARTED: 'Rozpoczęta',
        EMAIL_VERIFIED: 'Email zweryfikowany',
        PHONE_VERIFIED: 'Telefon zweryfikowany',
        ACCOUNT_ACTIVE: 'Konto aktywne',
        BASIC_DATA_COMPLETED: 'Dane podstawowe',
        PERSONAL_DATA_COMPLETED: 'Dane osobowe',
        ADDRESS_COMPLETED: 'Adres uzupełniony',
        DOCUMENTS_SUBMITTED: 'Dokumenty przesłane',
        DOCUMENTS_VERIFIED: 'Dokumenty zweryfikowane',
        ZUS_DECLARATIONS_COMPLETED: 'ZUS wypełniony',
        OWNER_PENDING_REVIEW: 'Właściciel — do weryfikacji',
        READY_TO_WORK: 'Zaakceptowany',
      },

      statusProgress: {
        REGISTRATION_STARTED: 5,
        EMAIL_VERIFIED: 15,
        PHONE_VERIFIED: 25,
        ACCOUNT_ACTIVE: 30,
        BASIC_DATA_COMPLETED: 45,
        PERSONAL_DATA_COMPLETED: 55,
        ADDRESS_COMPLETED: 65,
        DOCUMENTS_SUBMITTED: 80,
        DOCUMENTS_VERIFIED: 90,
        ZUS_DECLARATIONS_COMPLETED: 95,
        OWNER_PENDING_REVIEW: 90,
        READY_TO_WORK: 100,
      },

      docTypeLabels: {
        ID_FRONT: 'Dowód osobisty (przód)',
        ID_BACK: 'Dowód osobisty (tył)',
        RESIDENCE_CARD_FRONT: 'Karta pobytu (przód)',
        RESIDENCE_CARD_BACK: 'Karta pobytu (tył)',
        DRIVING_LICENSE: 'Prawo jazdy (przód)',
        DRIVING_LICENSE_BACK: 'Prawo jazdy (tył)',
        VEHICLE_REG_FRONT: 'Dowód rejestracyjny (przód)',
        VEHICLE_REG_BACK: 'Dowód rejestracyjny (tył)',
        PSYCHO_CERT: 'Zaświadczenie psychologiczne',
        MEDICAL_CERT: 'Zaświadczenie lekarskie',
        CRIMINAL_RECORD: 'Zaświadczenie o niekaralności',
        CRIMINAL_RECORD_FOREIGN: 'Zaświadczenie o niekaralności (zagraniczne)',
        CRIMINAL_RECORD_TRANSLATION: 'Tłumaczenie zaświadczenia o niekaralności',
        STUDENT_ID_FRONT: 'Legitymacja studencka (przód)',
        STUDENT_ID_BACK: 'Legitymacja studencka (tył)',
        PENSION_CERT: 'Zaświadczenie emerytalne',
        DISABILITY_CERT: 'Orzeczenie o niepełnosprawności',
        RENT_CERT: 'Zaświadczenie rentowe',
      },

      docTypeOptions: [
        { text: 'Karta pobytu (przód)', value: 'RESIDENCE_CARD_FRONT' },
        { text: 'Karta pobytu (tył)', value: 'RESIDENCE_CARD_BACK' },
        { text: 'Prawo jazdy (przód)', value: 'DRIVING_LICENSE' },
        { text: 'Prawo jazdy (tył)', value: 'DRIVING_LICENSE_BACK' },
        { text: 'Dowód rejestracyjny (przód)', value: 'VEHICLE_REG_FRONT' },
        { text: 'Dowód rejestracyjny (tył)', value: 'VEHICLE_REG_BACK' },
        { text: 'Zaświadczenie psychologiczne', value: 'PSYCHO_CERT' },
        { text: 'Zaświadczenie lekarskie', value: 'MEDICAL_CERT' },
        { text: 'Zaświadczenie o niekaralności', value: 'CRIMINAL_RECORD' },
        { text: 'Zaświadczenie o niekaralności (zagraniczne)', value: 'CRIMINAL_RECORD_FOREIGN' },
        { text: 'Tłumaczenie zaświadczenia o niekaralności', value: 'CRIMINAL_RECORD_TRANSLATION' },
        { text: 'Legitymacja studencka (przód)', value: 'STUDENT_ID_FRONT' },
        { text: 'Legitymacja studencka (tył)', value: 'STUDENT_ID_BACK' },
        { text: 'Zaświadczenie emerytalne', value: 'PENSION_CERT' },
        { text: 'Orzeczenie o niepełnosprawności', value: 'DISABILITY_CERT' },
        { text: 'Zaświadczenie rentowe', value: 'RENT_CERT' },
      ],

      consentLabels: {
        ADULT: 'Oświadczenie o pełnoletności',
        TERMS: 'Akceptacja regulaminu',
        REFERRAL: 'Zgoda na polecenie',
        MARKETING_PHONE: 'Marketing telefoniczny',
        MARKETING_EMAIL: 'Marketing e-mail',
      },
    };
  },

  watch: {
    options: {
      handler() {
        this.fetchDrivers();
      },
      deep: true,
    },
  },

  computed: {
    // Zdjęcia pojazdu i niekaralność — bez daty ważności.
    addDocMaWaznosc() {
      return maDateWaznosci(this.addDocForm.documentType);
    },
    contactTemplateOptions() {
      const wbudowane = this.builtinContactTemplates.map((tpl) => ({ id: tpl.id, name: tpl.name }));
      if (this.contactDialog.mode !== 'sms') return wbudowane;
      // Przy SMS-ach dokładamy szablony zapisane w zakładce SMS.
      const zapisane = this.savedSmsTemplates.map((tpl) => ({ id: 'saved:' + tpl.id, name: tpl.name + ' (zapisany)' }));
      return wbudowane.concat(zapisane);
    },
  },
  methods: {
    applyContactTemplate(id) {
      if (!id) return;
      const d = this.contactDialog.driver || {};
      const imie = d.firstName || '';
      const podstaw = (s) => String(s || '').replace(/\{imie\}/g, imie).replace(/\s{2,}/g, (m) => (m.includes('\n') ? m : ' ')).trim();
      if (id.startsWith('saved:')) {
        const tpl = this.savedSmsTemplates.find((x) => 'saved:' + x.id === id);
        if (tpl) this.contactDialog.message = podstaw(tpl.content);
        return;
      }
      const tpl = this.builtinContactTemplates.find((x) => x.id === id);
      if (!tpl) return;
      if (this.contactDialog.mode === 'sms') {
        this.contactDialog.message = podstaw(tpl.sms);
      } else {
        this.contactDialog.subject = tpl.emailSubject;
        this.contactDialog.message = podstaw(tpl.emailBody);
      }
    },
    async loadSavedSmsTemplates() {
      if (this.savedSmsTemplates.length) return;
      try {
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'sms/templates',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.savedSmsTemplates = res.data?.data || res.data || [];
      } catch (e) { /* szablony to wygoda — brak nie blokuje wysyłki */ }
    },
    openNoteDialog(item) {
      this.noteDraft = item.onboardingNote || '';
      this.noteDialog = { show: true, driver: item };
    },
    async saveOnboardingNote() {
      const target = this.noteDialog.driver;
      this.noteSaving = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/edit',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { driverId: target.id, onboardingNote: this.noteDraft },
        });
        this.$set(target, 'onboardingNote', this.noteDraft);
        if (this.selectedDriver && this.selectedDriver.id === target.id) {
          this.selectedDriver.onboardingNote = this.noteDraft;
        }
        this.noteDialog.show = false;
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się zapisać notatki', 'error');
      }
      this.noteSaving = false;
    },
    openContactDialog(mode, item) {
      if (mode === 'sms') this.loadSavedSmsTemplates();
      this.contactDialog = {
        show: true,
        mode,
        driver: item,
        subject: mode === 'email' ? 'BTP — Twoja rejestracja' : '',
        message: '',
        sending: false,
      };
    },
    async sendContact() {
      this.contactDialog.sending = true;
      const d = this.contactDialog.driver;
      try {
        if (this.contactDialog.mode === 'sms') {
          await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'sms/send',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            data: {
              driverId: d.id,
              phone: d.phone,
              // sms/send wymaga recipientName (IsNotEmpty) — bez niego 400.
              recipientName: `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.phone,
              message: this.contactDialog.message.trim(),
            },
          });
        } else {
          await axios({
            method: 'post',
            url: process.env.VUE_APP_ROOT_API + 'email/send',
            headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
            data: {
              driverId: d.id,
              email: d.email,
              recipientName: `${d.firstName || ''} ${d.lastName || ''}`.trim() || d.email,
              subject: this.contactDialog.subject.trim(),
              message: this.contactDialog.message.trim(),
            },
          });
        }
        this.contactDialog.show = false;
        Swal.fire({ icon: 'success', title: 'Wysłano', timer: 1800, showConfirmButton: false });
      } catch (e) {
        Swal.fire('Błąd', e.response?.data?.message?.toString() || 'Nie udało się wysłać', 'error');
      }
      this.contactDialog.sending = false;
    },

    debouncedSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.options.page = 1;
        this.fetchDrivers();
      }, 400);
    },

    async fetchDrivers() {
      this.loading = true;
      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/pending',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          params: {
            take: this.options.itemsPerPage,
            skip: this.options.page,
            search: this.search || undefined,
          },
        });

        const data = response.data?.data || {};
        this.drivers = data.result || [];
        this.totalItems = data.count || 0;
      } catch (error) {
        console.error('Error fetching onboarding drivers:', error);
        Swal.fire({ title: 'Błąd!', text: 'Nie udało się pobrać listy kierowców', icon: 'error' });
      } finally {
        this.loading = false;
      }
    },

    async viewDriver(item) {
      this.detailTab = 0;
      this.detailLoading = true;
      this.detailDialog = true;

      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/driver/' + item.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.selectedDriver = response.data?.data || item;
      } catch (error) {
        console.error('Error fetching driver detail:', error);
        this.selectedDriver = item;
      } finally {
        this.detailLoading = false;
      }
    },

    // Edit methods
    startEdit() {
      this.editForm = {
        firstName: this.selectedDriver.firstName || '',
        lastName: this.selectedDriver.lastName || '',
        email: this.selectedDriver.email || '',
        phone: this.selectedDriver.phone || '',
        citizenship: this.selectedDriver.citizenship || '',
        mainLanguage: this.selectedDriver.mainLanguage || '',
        hasOwnVehicle: this.selectedDriver.hasOwnVehicle || false,
        hasBusiness: this.selectedDriver.hasBusiness || false,
        streetAddress: this.selectedDriver.streetAddress || '',
        homeNumber: this.selectedDriver.homeNumber || '',
        aptNumber: this.selectedDriver.aptNumber || '',
        postalCode: this.selectedDriver.postalCode || '',
        city: this.selectedDriver.city || '',
        onboardingStatus: this.selectedDriver.onboardingStatus,
      };
      this.editMode = true;
    },

    cancelEdit() {
      this.editMode = false;
      this.editForm = {};
    },

    async saveEdit() {
      this.editSaving = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/edit',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: {
            driverId: this.selectedDriver.id,
            ...this.editForm,
          },
        });

        // Refresh driver detail
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/driver/' + this.selectedDriver.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.selectedDriver = response.data?.data || this.selectedDriver;
        this.editMode = false;

        Swal.fire({ title: 'Zapisano!', text: 'Dane kierowcy zostały zaktualizowane', icon: 'success', timer: 2000 });
        this.fetchDrivers();
      } catch (error) {
        console.error('Error saving driver data:', error);
        Swal.fire({ title: 'Błąd!', text: error.response?.data?.message || 'Nie udało się zapisać zmian', icon: 'error' });
      } finally {
        this.editSaving = false;
      }
    },

    openApproveDialog(item) {
      this.actionDriver = item;
      this.approveCompanyId = null;
      this.approveSubCompanyIds = [];
      this.approveDialog = true;
      this.fetchCompanies();
      this.fetchSubCompanies();
    },

    async fetchCompanies() {
      this.companiesLoading = true;
      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'company',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.companies = (response.data?.data || []).map(c => ({ id: c.id, name: c.name }));
        // Od 21.09.2026 są dwie firmy (BTP i 7Rolls) — wcześniej jedyna firma
        // wybierała się sama, potem pole zostawało puste, a przycisk
        // „Zaakceptuj” był nieaktywny bez słowa wyjaśnienia (14.10.2026,
        // Śmielecka i Cheran). Podstawiamy firmę kierowcy, a gdy jej nie ma —
        // firmę zalogowanego admina (z tokenu).
        const ids = this.companies.map(c => c.id);
        const kandydaci = [this.actionDriver && this.actionDriver.companyId, this.firmaAdmina(), this.companies.length === 1 ? ids[0] : null];
        this.approveCompanyId = kandydaci.find(id => id && ids.includes(id)) || null;
      } catch (e) {
        console.error('Error fetching companies:', e);
      } finally {
        this.companiesLoading = false;
      }
    },

    firmaAdmina() {
      try {
        const t = localStorage.getItem('token') || '';
        const dane = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return (dane.user && dane.user.companyId) || null;
      } catch (e) {
        return null;
      }
    },

    async fetchSubCompanies() {
      this.subCompaniesLoading = true;
      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'sub-company',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.subCompanies = (response.data?.data || []).map(c => ({ id: c.id, name: c.name }));
      } catch (e) {
        console.error('Error fetching subcompanies:', e);
      } finally {
        this.subCompaniesLoading = false;
      }
    },

    openRejectDialog(item) {
      this.actionDriver = item;
      this.rejectReason = '';
      this.rejectDialog = true;
    },

    async approveDriver() {
      if (!this.approveCompanyId) return;
      this.approveLoading = true;
      this.processing.push(this.actionDriver.id);

      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/approve',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: {
            driverId: this.actionDriver.id,
            companyId: this.approveCompanyId,
            subCompanyIds: this.approveSubCompanyIds,
          },
        });

        Swal.fire({ title: 'Zaakceptowano!', text: 'Kierowca został zaakceptowany', icon: 'success', timer: 2000 });
        this.approveDialog = false;
        this.detailDialog = false;
        this.fetchDrivers();
        this.$root.$emit('onboarding-updated');
      } catch (error) {
        console.error('Error approving driver:', error);
        Swal.fire({ title: 'Błąd!', text: error.response?.data?.message || 'Wystąpił błąd', icon: 'error' });
      } finally {
        this.approveLoading = false;
        this.processing = this.processing.filter(id => id !== this.actionDriver.id);
      }
    },

    openLeadDialog(driver) {
      this.actionDriver = driver;
      this.leadNote = '';
      this.leadDialog = true;
    },
    async moveDriverToLeads() {
      this.leadLoading = true;
      this.processing.push(this.actionDriver.id);
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/to-leads',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { driverId: this.actionDriver.id, note: this.leadNote || undefined },
        });
        Swal.fire({
          title: 'Przeniesiono',
          text: res.data?.data?.message || 'Kontakt zapisany w książce leadów.',
          icon: 'success',
          timer: 2600,
        });
        this.leadDialog = false;
        this.detailDialog = false;
        this.fetchDrivers();
        this.$root.$emit('onboarding-updated');
      } catch (error) {
        console.error('Error moving driver to leads:', error);
        Swal.fire({ title: 'Błąd!', text: error.response?.data?.message || 'Wystąpił błąd', icon: 'error' });
      } finally {
        this.leadLoading = false;
        this.processing = this.processing.filter(id => id !== this.actionDriver.id);
      }
    },
    async rejectDriver() {
      this.rejectLoading = true;
      this.processing.push(this.actionDriver.id);

      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/reject',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: {
            driverId: this.actionDriver.id,
            reason: this.rejectReason || undefined,
          },
        });

        Swal.fire({ title: 'Odrzucono!', text: 'Kierowca został odrzucony', icon: 'success', timer: 2000 });
        this.rejectDialog = false;
        this.detailDialog = false;
        this.fetchDrivers();
        this.$root.$emit('onboarding-updated');
      } catch (error) {
        console.error('Error rejecting driver:', error);
        Swal.fire({ title: 'Błąd!', text: error.response?.data?.message || 'Wystąpił błąd', icon: 'error' });
      } finally {
        this.rejectLoading = false;
        this.processing = this.processing.filter(id => id !== this.actionDriver.id);
      }
    },

    // Document actions
    async previewDocument(doc) {
      this.docLoading = doc.id;
      this.previewDocName = this.getDocTypeLabel(doc.documentType);
      this.previewLoading = true;
      this.previewDialog = true;

      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/document/file/' + doc.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });

        const data = response.data?.data || {};
        const fileUrl = data.fileUrl || '';
        const base64 = data.fileData;

        if (fileUrl.endsWith('.pdf') || fileUrl.includes('.pdf')) {
          this.previewType = 'pdf';
          this.previewSrc = 'data:application/pdf;base64,' + base64;
        } else {
          this.previewType = 'image';
          const ext = fileUrl.split('.').pop().toLowerCase();
          const mimeMap = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp' };
          const mime = mimeMap[ext] || 'image/jpeg';
          this.previewSrc = 'data:' + mime + ';base64,' + base64;
        }
      } catch (error) {
        console.error('Error loading document:', error);
        this.previewType = 'error';
        Swal.fire({ title: 'Błąd!', text: 'Nie udało się załadować dokumentu', icon: 'error' });
        this.previewDialog = false;
      } finally {
        this.docLoading = null;
        this.previewLoading = false;
      }
    },

    async verifyDocument(doc) {
      this.docProcessing.push(doc.id);
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/document/verify',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: { documentId: doc.id },
        });

        doc.status = 'VERIFIED';
        doc.rejectionReason = null;
      } catch (error) {
        console.error('Error verifying document:', error);
        Swal.fire({ title: 'Błąd!', text: 'Nie udało się zweryfikować dokumentu', icon: 'error' });
      } finally {
        this.docProcessing = this.docProcessing.filter(id => id !== doc.id);
      }
    },

    // Add document
    openAddDocDialog() {
      this.addDocForm = { documentType: null, file: null, documentNumber: '', expiryDate: '' };
      this.addDocDialog = true;
    },

    async uploadDocument() {
      if (!this.addDocForm.documentType || !this.addDocForm.file) return;
      this.addDocLoading = true;

      try {
        const formData = new FormData();
        formData.append('file', this.addDocForm.file);
        formData.append('driverId', this.selectedDriver.id);
        formData.append('documentType', this.addDocForm.documentType);
        if (this.addDocForm.documentNumber) formData.append('documentNumber', this.addDocForm.documentNumber);
        if (this.addDocMaWaznosc && this.addDocForm.expiryDate) formData.append('expiryDate', this.addDocForm.expiryDate);

        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/upload-document',
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('token'),
            'Content-Type': 'multipart/form-data',
          },
          data: formData,
        });

        this.addDocDialog = false;
        Swal.fire({ title: 'Przesłano!', text: 'Dokument został dodany', icon: 'success', timer: 2000 });

        // Refresh driver detail
        await this.refreshDriverDetail();
      } catch (error) {
        console.error('Error uploading document:', error);
        Swal.fire({ title: 'Błąd!', text: error.response?.data?.message || 'Nie udało się przesłać dokumentu', icon: 'error' });
      } finally {
        this.addDocLoading = false;
      }
    },

    // ZUS edit
    startZusEdit() {
      const zus = this.selectedDriver.zusDeclarations || {};
      this.zusForm = {
        isEmployedElsewhere: zus.isEmployedElsewhere || false,
        earnsAboveMinimum: zus.earnsAboveMinimum || false,
        isStudent: zus.isStudent || false,
        hasPensionRights: zus.hasPensionRights || false,
        hasRentRights: zus.hasRentRights || false,
        wantsVoluntarySickness: zus.wantsVoluntarySickness || false,
        isRegisteredUnemployed: zus.isRegisteredUnemployed || false,
        hasDisabilityCert: zus.hasDisabilityCert || false,
        taxOffice: zus.taxOffice || '',
        nfzBranch: zus.nfzBranch || '',
      };
      this.zusEditMode = true;
    },

    async saveZusEdit() {
      this.zusSaving = true;
      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/edit-zus',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: {
            driverId: this.selectedDriver.id,
            ...this.zusForm,
          },
        });

        this.zusEditMode = false;
        Swal.fire({ title: 'Zapisano!', text: 'Oświadczenia ZUS zaktualizowane', icon: 'success', timer: 2000 });
        await this.refreshDriverDetail();
      } catch (error) {
        console.error('Error saving ZUS:', error);
        Swal.fire({ title: 'Błąd!', text: error.response?.data?.message || 'Nie udało się zapisać', icon: 'error' });
      } finally {
        this.zusSaving = false;
      }
    },

    async refreshDriverDetail() {
      try {
        const response = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/driver/' + this.selectedDriver.id,
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        this.selectedDriver = response.data?.data || this.selectedDriver;
      } catch (e) {
        console.error('Error refreshing driver:', e);
      }
    },

    openRejectDocDialog(doc) {
      this.actionDocument = doc;
      this.docRejectReason = '';
      this.rejectDocDialog = true;
    },

    async rejectDocument() {
      if (!this.docRejectReason) return;
      this.docRejectLoading = true;
      this.docProcessing.push(this.actionDocument.id);

      try {
        await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/document/reject',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
          data: {
            documentId: this.actionDocument.id,
            reason: this.docRejectReason,
          },
        });

        this.actionDocument.status = 'REJECTED';
        this.actionDocument.rejectionReason = this.docRejectReason;
        this.rejectDocDialog = false;
      } catch (error) {
        console.error('Error rejecting document:', error);
        Swal.fire({ title: 'Błąd!', text: 'Nie udało się odrzucić dokumentu', icon: 'error' });
      } finally {
        this.docRejectLoading = false;
        this.docProcessing = this.docProcessing.filter(id => id !== this.actionDocument.id);
      }
    },

    // Helpers
    getStatusLabel(status) {
      return this.statusLabels[status] || status;
    },
    getStatusColor(status) {
      const colors = {
        REGISTRATION_STARTED: 'grey',
        EMAIL_VERIFIED: 'blue-grey',
        PHONE_VERIFIED: 'blue-grey',
        ACCOUNT_ACTIVE: 'blue',
        BASIC_DATA_COMPLETED: 'indigo',
        PERSONAL_DATA_COMPLETED: 'indigo',
        ADDRESS_COMPLETED: 'teal',
        DOCUMENTS_SUBMITTED: 'orange',
        DOCUMENTS_VERIFIED: 'green lighten-1',
        ZUS_DECLARATIONS_COMPLETED: 'amber darken-2',
        OWNER_PENDING_REVIEW: 'amber darken-3',
        READY_TO_WORK: 'success',
      };
      return colors[status] || 'grey';
    },
    getAvatarColor(status) {
      if (['DOCUMENTS_SUBMITTED', 'ZUS_DECLARATIONS_COMPLETED'].includes(status)) return 'orange';
      if (status === 'READY_TO_WORK') return 'success';
      return 'blue-grey';
    },
    getProgress(status) {
      return this.statusProgress[status] || 0;
    },
    getDocTypeLabel(type) {
      if (type && type.startsWith('zus_')) {
        return 'Dokument ZUS: ' + type.replace('zus_', '');
      }
      return this.docTypeLabels[type] || type;
    },
    getDocStatusColor(status) {
      return { PENDING: 'orange', VERIFIED: 'success', REJECTED: 'error' }[status] || 'grey';
    },
    getDocStatusLabel(status) {
      return { PENDING: 'Oczekujący', VERIFIED: 'Zweryfikowany', REJECTED: 'Odrzucony' }[status] || status;
    },
    getConsentLabel(type) {
      return this.consentLabels[type] || type;
    },
    formatDate(dateString) {
      if (!dateString) return '—';
      return new Date(dateString).toLocaleDateString('pl-PL', {
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
    },
    getRegisterTypeLabel(type) {
      const labels = {
        IM_DRIVER: 'Jestem kierowcą',
        RECOMENDATION: 'Z polecenia',
        OTHER_PARTNER: 'Od innego partnera',
        JOB_LOOKUP: 'Ogłoszenie o pracę',
        ADMIN: 'Dodany przez admina',
      };
      return labels[type] || type || '—';
    },
    formatTimeAgo(dateString) {
      const now = new Date();
      const date = new Date(dateString);
      const diffMin = Math.floor((now - date) / 60000);
      if (diffMin < 1) return 'przed chwilą';
      if (diffMin < 60) return `${diffMin} min temu`;
      const diffH = Math.floor(diffMin / 60);
      if (diffH < 24) return `${diffH} godz. temu`;
      const diffDays = Math.floor(diffH / 24);
      if (diffDays === 1) return 'wczoraj';
      if (diffDays < 7) return diffDays + ' dni temu';
      if (diffDays < 30) return Math.floor(diffDays / 7) + ' tyg. temu';
      return Math.floor(diffDays / 30) + ' mies. temu';
    },
  },

  mounted() {
    this.fetchDrivers();
  },
};
</script>

<style scoped>
.driver-onboarding-page {
  margin: -24px;
  padding: 24px;
  background: #181C28;
  min-height: calc(100vh - 64px);
  color: #E6EAF2;
}
.page-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 20px;
}
.page-eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #5478FF;
  font-weight: 600;
}
.page-title {
  font-size: 28px;
  font-weight: 700;
  margin: 4px 0 0 0;
  color: #FFFFFF;
}
.content-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 12px !important;
  border: 1px solid #2A3142 !important;
}
.driver-onboarding-page >>> .v-data-table {
  background: transparent !important;
  color: #E6EAF2 !important;
}
.driver-onboarding-page >>> .v-data-table thead tr th {
  background: #1F2433 !important;
  color: #94A0B8 !important;
  border-bottom: 1px solid #2A3142 !important;
}
.driver-onboarding-page >>> .v-data-table tbody tr td {
  border-bottom: 1px solid #2A3142 !important;
  color: #E6EAF2 !important;
}
.driver-onboarding-page >>> .v-data-table tbody tr:hover {
  background: rgba(84, 120, 255, 0.06) !important;
}
.driver-onboarding-page >>> .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.driver-onboarding-page >>> .v-text-field input,
.driver-onboarding-page >>> .v-text-field textarea,
.driver-onboarding-page >>> .v-select__selection {
  color: #E6EAF2 !important;
}
.driver-onboarding-page >>> .v-label {
  color: #94A0B8 !important;
}
.driver-onboarding-page >>> .v-card__title {
  color: #FFFFFF !important;
}
.driver-onboarding-page >>> .grey--text {
  color: #94A0B8 !important;
}
.driver-onboarding-page >>> .primary--text {
  color: #5478FF !important;
}
.detail-row {
  padding: 4px 0;
  font-size: 14px;
}
.detail-row .label {
  font-weight: 600;
  color: #94A0B8;
  min-width: 160px;
  display: inline-block;
}
.v-card {
  border-radius: 12px;
}
.v-data-table >>> .v-data-table__wrapper {
  border-radius: 8px;
}
</style>
